import { Alert, Button, Card, Col, Progress, Row, Typography } from 'antd'
import {
  CheckCircleOutlined,
  CloudServerOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  FieldTimeOutlined,
  GlobalOutlined,
  LockOutlined,
  ThunderboltOutlined,
  UnlockOutlined,
  WifiOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { DataTable, Loader } from '@kolab/dms-base/components/ui'
import { getDashboardStats } from '../api/device.api'
import { getDeviceActivitySummary, listPoolAvailability, listExpiringDevices } from '../api/report.api'
import { dashboardStatsKeys, reportKeys } from '../api/queryKeys'
import { StatusBadge } from '../components/common/StatusBadge'
import type { PoolAvailability, ExpiringDevice } from '../api/types'
import { useNavigate } from 'react-router-dom'
import { P, usePermissionStore } from '../rbac'
import { ROUTES } from '../utils/routes'

const { Text } = Typography

interface StatCardProps {
  icon: React.ReactNode
  label: string
  value: string | number
  sublabel?: string
  accent?: string
  progress?: { value: number; color: string }
  /** Makes the card a button (click / Enter / Space). */
  onClick?: () => void
}

function StatCard({ icon, label, value, sublabel, accent = '#1677ff', progress, onClick }: StatCardProps) {
  return (
    <Card
      hoverable={!!onClick}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `${label}: ${value}` : undefined}
      style={{
        borderRadius: 12,
        border: 'none',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
        height: '100%',
        cursor: onClick ? 'pointer' : undefined,
      }}
      styles={{ body: { padding: '20px 24px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: `${accent}10`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            color: accent,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: 12, color: '#64748b', fontWeight: 500, letterSpacing: '0.02em' }}>
            {label}
          </Text>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#0f172a', lineHeight: 1.2, marginTop: 2 }}>
            {value}
          </div>
          {sublabel && (
            <Text style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, display: 'block' }}>
              {sublabel}
            </Text>
          )}
          {progress && (
            <Progress
              percent={progress.value}
              strokeColor={progress.color}
              showInfo={false}
              size="small"
              style={{ marginTop: 8, marginBottom: 0 }}
            />
          )}
        </div>
      </div>
    </Card>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissionStore()

  // Per-widget permission gates. Each card's data source is only fetched when
  // the caller holds the corresponding permission — otherwise the backend
  // would 403 and the axios interceptor would surface a red toast on every
  // dashboard visit for users (e.g. Reseller Operator) who legitimately
  // cannot see pool availability.
  //
  // The card permissions are independent — a user may hold any subset,
  // or none. When they hold none the page still renders a page-scope
  // "nothing to show" panel rather than an empty div (see the render path
  // below), so navigating to /dashboard with no card perms is a defined
  // no-op instead of a mysterious blank page.
  const canSeeDeviceStats = hasPermission(P.dashboard.device_stats_card.view)
  const canSeePoolCard = hasPermission(P.dashboard.pool_availability_card.view)
  const canSeeExpirationsCard = hasPermission(P.dashboard.upcoming_expirations_card.view)
  const canSeeActivityCard = hasPermission(P.dashboard.device_activity_card.view)
  const canViewInstallations = hasPermission(P.reports.device_installation.view)
  const canViewLockUnlock = hasPermission(P.reports.device_lock_unlock.view)
  const canViewDeviceList = hasPermission(P.device_operations.device_list.view)

  const deviceStatsQuery = useQuery({
    queryKey: dashboardStatsKeys.detail(),
    queryFn: getDashboardStats,
    enabled: canSeeDeviceStats,
  })

  const poolsQuery = useQuery({
    queryKey: reportKeys.pools.list({ pageSize: 20 }),
    queryFn: () => listPoolAvailability({ pageSize: 20 }),
    enabled: canSeePoolCard,
  })

  const expiringQuery = useQuery({
    queryKey: reportKeys.expiring.list({ pageSize: 10 }),
    queryFn: () => listExpiringDevices({ pageSize: 10 }),
    enabled: canSeeExpirationsCard,
  })

  const activityQuery = useQuery({
    queryKey: reportKeys.activitySummary(),
    queryFn: getDeviceActivitySummary,
    enabled: canSeeActivityCard,
  })

  if (deviceStatsQuery.isLoading && poolsQuery.isLoading) return <Loader />

  const stats = deviceStatsQuery.data
  const pools = poolsQuery.data?.items ?? []
  const expiring = expiringQuery.data?.items ?? []
  const activity = activityQuery.data

  // A card opens its report pre-filtered, but only when the user can view
  // that report — otherwise it stays a plain, non-interactive number.
  const openReport = (allowed: boolean, path: string, params?: Record<string, string>) =>
    allowed ? () => navigate(params ? `${path}?${new URLSearchParams(params)}` : path) : undefined
  const today = activity?.date ?? ''

  const totalCapacity = pools.reduce((s, p) => s + p.totalDeviceLimit, 0)
  const totalAllocated = pools.reduce((s, p) => s + p.allocatedDeviceLimit, 0)
  const totalAvailable = pools.reduce((s, p) => s + p.availableDeviceLimit, 0)
  const utilizationPct = totalCapacity > 0 ? Math.round((totalAllocated / totalCapacity) * 100) : 0

  const poolColumns: ColumnsType<PoolAvailability> = [
    { title: 'License Key', dataIndex: 'licenseKey', key: 'licenseKey', ellipsis: true },
    { title: 'Type', dataIndex: 'licenseType', key: 'licenseType', width: 120 },
    { title: 'Total', dataIndex: 'totalDeviceLimit', key: 'totalDeviceLimit', align: 'right', width: 80 },
    { title: 'Allocated', dataIndex: 'allocatedDeviceLimit', key: 'allocatedDeviceLimit', align: 'right', width: 100 },
    { title: 'Available', dataIndex: 'availableDeviceLimit', key: 'availableDeviceLimit', align: 'right', width: 100 },
    { title: 'Expiry', dataIndex: 'expiryDate', key: 'expiryDate', width: 110 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string) => <StatusBadge status={s} />,
    },
  ]

  const expiringColumns: ColumnsType<ExpiringDevice> = [
    { title: 'Device ID', dataIndex: 'deviceIdentifier', key: 'deviceIdentifier', ellipsis: true },
    { title: 'Reseller', dataIndex: 'resellerName', key: 'resellerName', ellipsis: true },
    { title: 'Expiry', dataIndex: 'deviceExpiryDate', key: 'deviceExpiryDate', width: 110 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string) => <StatusBadge status={s} />,
    },
  ]

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ marginBottom: 24 }}>
        <Text style={{ color: '#64748b', fontSize: 14 }}>
          System overview — devices, license pools, and activity at a glance.
        </Text>
      </div>

      {/* Device Operations Stats — gated by dashboard.device_stats_card.view */}
      {canSeeDeviceStats && stats && (
        <>
          <Text
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: '#94a3b8',
              display: 'block',
              marginBottom: 12,
            }}
          >
            Device Activity
          </Text>
          <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
            <Col xs={12} sm={12} md={8} lg={4}>
              <StatCard
                icon={<DesktopOutlined />}
                label="Total Devices"
                value={stats.totalDevices}
                accent="#6366f1"
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={4}>
              <StatCard
                icon={<CloudServerOutlined />}
                label="Online Now"
                value={stats.onlineDevices}
                sublabel={stats.totalDevices > 0
                  ? `${Math.round((stats.onlineDevices / stats.totalDevices) * 100)}% of fleet`
                  : undefined}
                accent="#10b981"
                progress={stats.totalDevices > 0 ? {
                  value: Math.round((stats.onlineDevices / stats.totalDevices) * 100),
                  color: '#10b981',
                } : undefined}
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={4}>
              <StatCard
                icon={<WifiOutlined />}
                label="Total Pings"
                value={stats.totalPings.toLocaleString()}
                accent="#0ea5e9"
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={4}>
              <StatCard
                icon={<ThunderboltOutlined />}
                label="Recent Pings"
                value={stats.recentPings.toLocaleString()}
                sublabel="Last 24 hours"
                accent="#f59e0b"
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={4}>
              <StatCard
                icon={<GlobalOutlined />}
                label="Cached Locations"
                value={stats.cachedLocations}
                accent="#8b5cf6"
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={4}>
              <StatCard
                icon={<DashboardOutlined />}
                label="Last Sync"
                value={stats.lastUpdated ? new Date(stats.lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                sublabel={stats.lastUpdated ? new Date(stats.lastUpdated).toLocaleDateString() : undefined}
                accent="#64748b"
              />
            </Col>
          </Row>
        </>
      )}

      {/* Installations & Lock/Unlock — gated by dashboard.device_activity_card.view.
          Counts are for "today" in the server's report timezone; each card's
          report link uses the same day so the card and the report agree. */}
      {canSeeActivityCard && (
        <>
          <Text
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: '#94a3b8',
              display: 'block',
              marginBottom: 12,
            }}
          >
            Installations & Lock/Unlock{activity ? ` · ${activity.date} (${activity.timezone})` : ''}
          </Text>
          {activityQuery.isLoading ? (
            <Loader />
          ) : activityQuery.isError || !activity ? (
            <Alert
              type="error"
              showIcon
              title="Unable to load device activity."
              action={<Button size="small" onClick={() => activityQuery.refetch()}>Retry</Button>}
              style={{ marginBottom: 28 }}
            />
          ) : (
            <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
              <Col xs={12} sm={12} md={8} lg={4}>
                <StatCard
                  icon={<DesktopOutlined />}
                  label="Total Devices"
                  value={activity.totalDevices.toLocaleString()}
                  accent="#6366f1"
                  onClick={openReport(canViewDeviceList, '/device-ops')}
                />
              </Col>
              <Col xs={12} sm={12} md={8} lg={5}>
                <StatCard
                  icon={<CheckCircleOutlined />}
                  label="Devices Installed Today"
                  value={activity.installedToday.toLocaleString()}
                  accent="#10b981"
                  onClick={openReport(canViewInstallations, ROUTES.reportsDeviceInstallation, { fromDate: today, toDate: today })}
                />
              </Col>
              <Col xs={12} sm={12} md={8} lg={5}>
                <StatCard
                  icon={<CloudServerOutlined />}
                  label="Total Devices Installed"
                  value={activity.totalDevicesInstalled.toLocaleString()}
                  accent="#0ea5e9"
                  onClick={openReport(canViewInstallations, ROUTES.reportsDeviceInstallation)}
                />
              </Col>
              <Col xs={12} sm={12} md={12} lg={5}>
                <StatCard
                  icon={<LockOutlined />}
                  label="Locks Today"
                  value={activity.successfulLocksToday.toLocaleString()}
                  sublabel="Successful"
                  accent="#ef4444"
                  onClick={openReport(canViewLockUnlock, ROUTES.reportsDeviceLockUnlock, { fromDate: today, toDate: today, action: 'LOCK' })}
                />
              </Col>
              <Col xs={12} sm={12} md={12} lg={5}>
                <StatCard
                  icon={<UnlockOutlined />}
                  label="Unlocks Today"
                  value={activity.successfulUnlocksToday.toLocaleString()}
                  sublabel="Successful"
                  accent="#f59e0b"
                  onClick={openReport(canViewLockUnlock, ROUTES.reportsDeviceLockUnlock, { fromDate: today, toDate: today, action: 'UNLOCK' })}
                />
              </Col>
            </Row>
          )}
        </>
      )}

      {/* License Pool Stats + Pool availability table — gated by dashboard.pool_availability_card.view */}
      {canSeePoolCard && (
        <>
          <Text
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: '#94a3b8',
              display: 'block',
              marginBottom: 12,
            }}
          >
            License Pools
          </Text>
          <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
            <Col xs={24} sm={8}>
              <StatCard
                icon={<DatabaseOutlined />}
                label="Total Capacity"
                value={totalCapacity.toLocaleString()}
                sublabel="Across all pools"
                accent="#6366f1"
              />
            </Col>
            <Col xs={24} sm={8}>
              <StatCard
                icon={<DashboardOutlined />}
                label="Allocated"
                value={totalAllocated.toLocaleString()}
                accent="#f59e0b"
                progress={{ value: utilizationPct, color: utilizationPct > 85 ? '#ef4444' : '#f59e0b' }}
                sublabel={`${utilizationPct}% utilization`}
              />
            </Col>
            <Col xs={24} sm={8}>
              <StatCard
                icon={<CloudServerOutlined />}
                label="Available"
                value={totalAvailable.toLocaleString()}
                sublabel="Ready to allocate"
                accent="#10b981"
              />
            </Col>
          </Row>
        </>
      )}

      {/* Tables — each half is independently gated */}
      {(canSeePoolCard || canSeeExpirationsCard) && (
        <Row gutter={[16, 16]}>
          {canSeePoolCard && (
            <Col xs={24} lg={canSeeExpirationsCard ? 14 : 24}>
              <Card
                title={
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                    Pool availability
                  </span>
                }
                extra={
                  hasPermission(P.reports.pool_availability.view) ? (
                    <a
                      onClick={() => navigate('/reports/pool-availability')}
                      style={{ fontSize: 12, color: '#6366f1', cursor: 'pointer' }}
                    >
                      View all
                    </a>
                  ) : null
                }
                style={{
                  borderRadius: 12,
                  border: 'none',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
                }}
                styles={{ body: { padding: '0 4px' } }}
              >
                {poolsQuery.isLoading ? (
                  <Loader />
                ) : (
                  <DataTable<PoolAvailability>
                    rowKey="distributorLicenseId"
                    columns={poolColumns}
                    dataSource={pools.slice(0, 8)}
                    pagination={false}
                    scrollHeight={320}
                  />
                )}
              </Card>
            </Col>
          )}
          {canSeeExpirationsCard && (
            <Col xs={24} lg={canSeePoolCard ? 10 : 24}>
              <Card
                title={
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                    <FieldTimeOutlined style={{ marginRight: 8, color: '#f59e0b' }} />
                    Upcoming expirations
                  </span>
                }
                extra={
                  hasPermission(P.reports.expiring_devices.view) ? (
                    <a
                      onClick={() => navigate('/reports/expiring-devices')}
                      style={{ fontSize: 12, color: '#6366f1', cursor: 'pointer' }}
                    >
                      View all
                    </a>
                  ) : null
                }
                style={{
                  borderRadius: 12,
                  border: 'none',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
                }}
                styles={{ body: { padding: '0 4px' } }}
              >
                {expiringQuery.isLoading ? (
                  <Loader />
                ) : expiring.length === 0 ? (
                  <div style={{ padding: '40px 24px', textAlign: 'center', color: '#94a3b8' }}>
                    <FieldTimeOutlined style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
                    No devices expiring soon
                  </div>
                ) : (
                  <DataTable<ExpiringDevice>
                    rowKey="id"
                    columns={expiringColumns}
                    dataSource={expiring}
                    pagination={false}
                    scrollHeight={320}
                  />
                )}
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* Empty-state fallback: user reached /dashboard but has none of the
          card permissions. Rare in production (menu would have hidden
          the entry) but possible via direct URL. */}
      {!canSeeDeviceStats && !canSeePoolCard && !canSeeExpirationsCard && !canSeeActivityCard && (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#94a3b8' }}>
          <DashboardOutlined style={{ fontSize: 36, display: 'block', marginBottom: 12 }} />
          Nothing to show on the dashboard for your role.
        </div>
      )}
    </div>
  )
}
